@if (mode === 'page') {
  <div class="ld-page" [attr.dir]="direction">

    <!-- Reading Progress Bar -->
    <div class="ld-progress" aria-hidden="true">
      <div class="ld-progress__fill" [style.width.%]="scrollProgress"></div>
    </div>

    <!-- Hero Header -->
    <header class="ld-hero">
      <div class="ld-hero__container">

        <!-- Top nav row: logo left, badge + print right -->
        <div class="ld-hero__topbar">
          <a routerLink="/" class="ld-hero__logo-link">
            <img src="assets/images/logo-light.svg" alt="Deben" class="ld-hero__logo" height="42">
          </a>
          <div class="ld-hero__topbar-actions">
            <div class="ld-hero__badge">
              @if (documentType === 'privacy') {
                <i class="ri-shield-check-line"></i>
                {{ 'COMMON.PRIVACY_POILICY' | translate }}
              } @else {
                <i class="ri-file-text-line"></i>
                {{ 'COMMON.TERMS_OF_USE' | translate }}
              }
            </div>
            <button class="ld-hero__print"
                    (click)="printDocument()"
                    type="button"
                    [disabled]="isLoading || isExportingPdf || !documentHtml"
                    [attr.aria-busy]="isExportingPdf">
              <i [class.ri-printer-line]="!isExportingPdf"
                 [class.ri-loader-4-line]="isExportingPdf"
                 [class.ld-hero__print-icon--spin]="isExportingPdf"></i>
              @if (isExportingPdf) {
                {{ 'COMMON.EXPORTING' | translate }} {{ 'COMMON.PDF' | translate }}
              } @else {
                {{ 'LEGAL_DOCUMENT.PRINT' | translate }}
              }
            </button>
          </div>
        </div>

        <!-- Separator -->
        <div class="ld-hero__divider" aria-hidden="true"></div>

        <!-- Document identity block — centered -->
        <div class="ld-hero__body">
          <h1 class="ld-hero__title">{{ title }}</h1>
          <span class="ld-hero__date">
            <i class="ri-calendar-line"></i>
            {{ lastUpdated }}
          </span>
        </div>

      </div>
      <div class="ld-hero__wave" aria-hidden="true">
        <svg viewBox="0 0 1440 56" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M 0,4 C 144,13 432,48 720,49 C 1008,50 1296,17 1440,9L1440 56L0 56z" fill="#eef4f7"/>
        </svg>
      </div>
    </header>

    <!-- Body -->
    <div class="ld-body">
      <div class="ld-body__container" [class.ld-body__container--no-sidebar]="tableOfContents.length === 0">

        <!-- Sidebar TOC -->
        @if (tableOfContents.length > 0) {
          <aside class="ld-toc">
            <div class="ld-toc__card">
              <div class="ld-toc__head">
                <i class="ri-list-check-2"></i>
                <span>{{ 'LEGAL_DOCUMENT.CONTENTS' | translate }}</span>
              </div>
              <nav class="ld-toc__nav" [attr.aria-label]="'LEGAL_DOCUMENT.CONTENTS' | translate">
                @for (section of tableOfContents; track section.id; let i = $index) {
                  <a class="ld-toc__link"
                     [attr.href]="'#' + section.id"
                     [class.ld-toc__link--active]="activeSection === section.id"
                     (click)="scrollToSection($event, section.id)"
                     [attr.aria-current]="activeSection === section.id ? 'location' : null">
                    <span class="ld-toc__num">{{ i + 1 }}</span>
                    <span class="ld-toc__label">{{ section.title }}</span>
                  </a>
                }
              </nav>
            </div>
          </aside>
        }

        <!-- Main Document -->
        <main class="ld-main">
          <div class="ld-card">
            <div class="ld-doc" [attr.dir]="direction">
              @if (isLoading) {
                <div class="ld-skeleton" aria-busy="true" [attr.aria-label]="'COMMON.LOADING' | translate">
                  <div class="ld-sk-h"></div>
                  <div class="ld-sk-p"></div>
                  <div class="ld-sk-p ld-sk-p--w80"></div>
                  <div class="ld-sk-p ld-sk-p--w90"></div>
                  <div class="ld-sk-h ld-sk-h--sm mt-5"></div>
                  <div class="ld-sk-p"></div>
                  <div class="ld-sk-p ld-sk-p--w85"></div>
                  <div class="ld-sk-p ld-sk-p--w70"></div>
                  <div class="ld-sk-h ld-sk-h--sm mt-5"></div>
                  <div class="ld-sk-p"></div>
                  <div class="ld-sk-p ld-sk-p--w75"></div>
                  <div class="ld-sk-p ld-sk-p--w80"></div>
                  <div class="ld-sk-h ld-sk-h--sm mt-5"></div>
                  <div class="ld-sk-p ld-sk-p--w90"></div>
                  <div class="ld-sk-p ld-sk-p--w65"></div>
                </div>
              } @else {
                <div [innerHTML]="documentHtml"></div>
              }
            </div>
          </div>

          <!-- Document Info Panel -->
          <div class="ld-info-panel">
            <div class="ld-info-panel__item">
              <i class="ri-time-line"></i>
              <div>
                <span class="ld-info-panel__label">{{ 'LEGAL_DOCUMENT.LAST_UPDATED' | translate }}</span>
                <span class="ld-info-panel__value">{{ lastUpdatedValue }}</span>
              </div>
            </div>
            <div class="ld-info-panel__divider"></div>
            <div class="ld-info-panel__item">
              <i class="ri-building-line"></i>
              <div>
                <span class="ld-info-panel__label">{{ 'COMMON.COMPANY' | translate }}</span>
                <span class="ld-info-panel__value">Al Omla Al Hadesa (AOH)</span>
              </div>
            </div>
            <div class="ld-info-panel__divider"></div>
            <div class="ld-info-panel__item ld-info-panel__item--links">
              <i class="ri-links-line"></i>
              <div>
                <span class="ld-info-panel__label">{{ 'LEGAL_DOCUMENT.RELATED' | translate }}</span>
                <div class="ld-info-panel__related">
                  @if (documentType !== 'terms') {
                    <a routerLink="/terms-and-conditions" target="_blank" rel="noopener">
                      {{'COMMON.TERMS_OF_USE' | translate}}
                    </a>
                  }
                  @if (documentType !== 'privacy') {
                    <a routerLink="/privacy-policy" target="_blank" rel="noopener">
                      {{'COMMON.PRIVACY_POILICY' | translate}}
                    </a>
                  }
                </div>
              </div>
            </div>
          </div>
        </main>

      </div>
    </div>

    <!-- Back to Top -->
    <button class="ld-btt"
            [class.ld-btt--show]="showBackToTop"
            (click)="scrollToTop()"
            type="button"
            [attr.aria-label]="'LEGAL_DOCUMENT.BACK_TO_TOP' | translate">
      <i class="ri-arrow-up-line"></i>
    </button>

    <!-- Footer -->
    <footer class="ld-footer">
      <div class="ld-footer__container">
        <div class="ld-footer__top">
          <img src="assets/images/logo-light.svg" alt="Deben" class="ld-footer__logo" height="28">
          <div class="ld-footer__nav">
            <a routerLink="/terms-and-conditions" target="_blank" rel="noopener" class="ld-footer__link">
              {{'COMMON.TERMS_OF_USE' | translate}}
            </a>
            <a routerLink="/privacy-policy" target="_blank" rel="noopener" class="ld-footer__link">
              {{'COMMON.PRIVACY_POILICY' | translate}}
            </a>
          </div>
        </div>
        <div class="ld-footer__bottom">
          <p class="ld-footer__copy">
            &copy; {{year}} {{ 'COMMON.DEBEN' | translate }} {{ 'COMMON.All_RIGHTS_RESERVED' | translate }}
          </p>
          <p class="ld-footer__recaptcha">
            {{'COMMON.PROTECTED_BY' | translate}} {{'COMMON.RECAPTCHA' | translate}}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener">
              {{'COMMON.GOOGLE_PRIVACY_POILICY' | translate}}
            </a>
            <span>&nbsp;{{'COMMON.AND' | translate}}&nbsp;</span>
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener">
              {{'COMMON.TERMS_OF_SERVICE' | translate}}
            </a>
            <span>{{'COMMON.TERMS_APPLY_EN' | translate}}</span>
          </p>
        </div>
      </div>
    </footer>

  </div>
} @else {
  <div class="legal-document-content" [attr.dir]="direction">
    @if (isLoading) {
      <div class="d-flex align-items-center justify-content-center py-4">
        <output class="spinner-border text-primary" aria-live="polite" [attr.aria-label]="'COMMON.LOADING' | translate">
          <span class="visually-hidden">{{ 'COMMON.LOADING' | translate }}</span>
        </output>
      </div>
    } @else {
      <div [innerHTML]="documentHtml"></div>
    }
  </div>
}
